import React, { useEffect, useState } from 'react';

interface DrunkVisualPuzzleProps {
  visualContent?: string;
  visualItems?: string[];
  visualKind?: 'emoji-grid' | 'odd-one-out' | 'symbol-sequence' | 'word-trap' | 'memory' | 'still-object';
  visualBehavior?: 'memory' | 'still-object';
  stillItemIndex?: number;
  memoryHideAfterMs?: number;
  memoryRevealAtMs?: number;
  memoryRevealDurationMs?: number;
  questionStartTime?: number;
  timeLimit?: number;
  animationClass?: string;
}

export const DrunkVisualPuzzle: React.FC<DrunkVisualPuzzleProps> = ({
  visualContent,
  visualItems,
  visualKind = 'emoji-grid',
  visualBehavior,
  stillItemIndex,
  memoryHideAfterMs = 5000,
  memoryRevealAtMs = 8000,
  memoryRevealDurationMs = 2000,
  questionStartTime,
  animationClass = '',
}) => {
  const getMemoryVisibility = (): boolean => {
    if (visualBehavior !== 'memory') return true;
    const elapsed = questionStartTime ? Math.max(0, Date.now() - questionStartTime) : 0;
    return elapsed < memoryHideAfterMs
      || (elapsed >= memoryRevealAtMs && elapsed < memoryRevealAtMs + memoryRevealDurationMs);
  };
  const [memoryVisible, setMemoryVisible] = useState(getMemoryVisibility);

  useEffect(() => {
    if (visualBehavior !== 'memory') {
      setMemoryVisible(true);
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];
    const start = questionStartTime ?? Date.now();
    const schedule = (atMs: number, visible: boolean) => {
      const delay = start + atMs - Date.now();
      if (delay <= 0) return;
      timers.push(setTimeout(() => setMemoryVisible(visible), delay));
    };

    setMemoryVisible(getMemoryVisibility());
    schedule(memoryHideAfterMs, false);
    schedule(memoryRevealAtMs, true);
    schedule(memoryRevealAtMs + memoryRevealDurationMs, false);
    return () => timers.forEach(clearTimeout);
  }, [
    visualBehavior,
    questionStartTime,
    memoryHideAfterMs,
    memoryRevealAtMs,
    memoryRevealDurationMs,
  ]);

  if (!visualContent && !visualItems?.length) return null;

  const behaviorClass = visualBehavior ? ` drunk-visual-puzzle--${visualBehavior}` : '';
  const containerAnimation = visualBehavior ? '' : ` ${animationClass}`;

  return (
    <div
      className={`drunk-visual-puzzle drunk-visual-puzzle--${visualKind}${behaviorClass}${containerAnimation}${memoryVisible ? '' : ' drunk-visual-puzzle--memory-hidden'}`}
      aria-label={visualContent}
    >
      {visualItems?.length ? (
        <div className="drunk-visual-puzzle__items">
          {visualItems.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className={
                visualBehavior === 'still-object' && index !== stillItemIndex
                  ? `drunk-visual-puzzle__item drunk-visual-puzzle__item--moving drunk-visual-puzzle__item--motion-${index % 3}`
                  : 'drunk-visual-puzzle__item'
              }
            >
              {item}
            </span>
          ))}
        </div>
      ) : visualContent}
    </div>
  );
};
